import { useEffect, useRef, useState } from "react";
import { X, FileText, ShieldCheck } from "lucide-react";
import toast from "react-hot-toast";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../../../components/ui/input-otp";
import {
  useStartTsEcMutation,
  useVerifyTsEcOtpMutation,
} from "../../../redux/features/propertyQuery";
import { AxiosPropertyInstance } from "../../../common/utils/AxiosInstance";
import ModalWrapper from "./ModalWeapper";
import { apiMsg, msg } from "../../../common/utils/messages";

/**
 * Instant EC — OTP + result only. Telangana (tgigrs urban EC) by default;
 * `stateId="andhra-pradesh"` drives the AP flow (/ap-ec), which has the same
 * contract but never asks for the one-time account OTP.
 *
 * The document no / year / SRO come from the EC request form; this modal only
 * sends the OTP to the given mobile, verifies it, and hands the request id back
 * via onEcReady so the caller places the order (the EC fetch runs server-side).
 *
 * Required props: docNo, year, sroCode, sroName, mobile, onEcReady. Optional: stateId.
 *
 * `property` (Telangana non-agriculture only) switches to the search BY
 * PROPERTY: { distCode, mandalCode, villageCode, villageName, surveyNo, plotNo,
 * houseNo }. After the OTP the portal answers with the matching documents;
 * 0 → "not-found" (offers one retry without the plot number when a plot was
 * given), over 100 → "too-many". Neither places an order — the customer edits
 * the details instead. Only a usable match goes on to "done".
 * Andhra Pradesh: `property` = { villageCode (REG_VILLAGE_DIVISION), villageName,
 * surveyNo?, plotNo?, houseNo? }. A house no searches the portal's "Building
 * (OR) Structure" records (non-agriculture); otherwise survey/plot searches
 * "Sites (OR) Agricultural Lands". The AP portal lists the documents BEFORE
 * the OTP, so "not-found" / "too-many" arrive from the status poll and no OTP
 * is sent for them.
 * On open it immediately sends the OTP (start). Stages: account-otp -> search-otp -> done.
 *
 * The customer must never be stuck here. When the caller passes
 * onPlaceWithoutEc(reason) the order is placed WITHOUT the OTP (the admin team
 * fetches the EC by hand) whenever the OTP route fails:
 *  - "server-down"      the portal health check says it is down (Telangana), or
 *                       the portal went down mid-flow — placed automatically;
 *  - "otp-not-sent"     the OTP was not out within SEND_WAIT_MS, or sending
 *                       failed — placed automatically;
 *  - "otp-not-received" the customer chose it from "Didn't get the OTP?",
 *                       offered OTP_HELP_AFTER_S after the OTP box appears (or
 *                       straight after a failed verify), next to Resend OTP.
 * Without that prop every failure is shown as an error.
 */
// What the backend puts in the error's `description` when the portal went down
// between sending the OTP and verifying it.
const GOVT_SERVER_DOWN = "GOVT_SERVER_DOWN";
// How long the customer waits for the OTP to be sent before the order is
// placed without it.
const SEND_WAIT_MS = 25000;
// Seconds after the OTP box appears before "Didn't get the OTP?" is offered.
const OTP_HELP_AFTER_S = 25;

// Message key (backend wording) per reason the order goes without the OTP.
const WITHOUT_OTP_KEYS = {
  "server-down": "EC.SERVER_DOWN",
  "otp-not-sent": "EC.OTP_NOT_SENT",
  "otp-not-received": "EC.WITHOUT_OTP",
};
// Marks our own give-up on a hanging start request (not a backend error).
const START_TIMEOUT = "START_TIMEOUT";

// Per state: the backend flow's base path, the header label, and whether it
// has a portal health check.
const EC_FLOWS = {
  telangana: { base: "ts-ec", label: "Telangana", health: true },
  "andhra-pradesh": { base: "ap-ec", label: "Andhra Pradesh", health: false },
};

const spinner = (
  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
  </svg>
);

export default function TelanganaEcInstant({
  open,
  onClose,
  onEcReady,
  onPlaceWithoutEc,
  docNo,
  year,
  sroCode,
  sroName,
  mobile,
  stateId = "telangana",
  property = null,
}) {
  const flow = EC_FLOWS[stateId] || EC_FLOWS.telangana;
  const isAp = stateId === "andhra-pradesh";
  const [stage, setStage] = useState("checking"); // checking | sending | account-otp | search-otp | done | placing | error | not-found | too-many | needs-detail
  const [requestId, setRequestId] = useState(null);
  const [otp, setOtp] = useState("");
  const [note, setNote] = useState("");
  // Seconds left before "Didn't get the OTP?" shows; 0 = showing.
  const [helpIn, setHelpIn] = useState(OTP_HELP_AFTER_S);

  const [startTsEc, { isLoading: starting }] = useStartTsEcMutation();
  const [verifyTsEcOtp, { isLoading: verifying }] = useVerifyTsEcOtpMutation();
  // Guards against React StrictMode double-invoking the open effect in dev,
  // which would fire two /start calls → two OTPs.
  const startedRef = useRef(false);
  // Bumped by every send (and by Resend), so a superseded poll loop stops.
  const runRef = useRef(0);
  // The order is placed without the OTP at most once.
  const placedRef = useRef(false);
  // Property search: set after a plot search found nothing and the customer
  // chose to search the survey number alone.
  const withoutPlotRef = useRef(false);
  const [docCount, setDocCount] = useState(0);
  // A Telangana property search needs a plot or door number (a survey number
  // alone can match over a thousand deeds). Missing either is the customer's
  // detail to fix — never a reason to place the order without the OTP. AP
  // searches by survey alone and reports "too-many" itself.
  function needsPropertyDetail() {
    if (!property) return false;
    const has = (v) => !!String(v || "").trim();
    if (isAp) return !has(property.houseNo) && !has(property.plotNo) && !has(property.surveyNo);
    return !has(property.plotNo) && !has(property.houseNo);
  }
  const startBody = () =>
    property
      ? {
          mode: "property",
          distCode: property.distCode,
          mandalCode: property.mandalCode,
          villageCode: property.villageCode,
          surveyNo: property.surveyNo,
          plotNo: withoutPlotRef.current ? "" : property.plotNo,
          houseNo: withoutPlotRef.current ? "" : property.houseNo,
          surveyOnly: withoutPlotRef.current,
          mobile,
        }
      : { docNo, year, sroCode, sroName, mobile };
  // Mirrors `open` so the poll loop can stop on a REAL close without a
  // per-effect cancel flag (StrictMode's cleanup would wrongly trip that and
  // kill the in-flight request, leaving the OTP box hidden after the OTP sent).
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  // The backend's line for a failed call, else the message for `fallbackKey`.
  const apiError = (err, fallbackKey) =>
    err?.data?.status?.message ||
    err?.data?.message ||
    apiMsg(err?.data?.status, fallbackKey);

  const token = () => {
    try {
      return JSON.parse(localStorage.getItem("token"));
    } catch {
      return null;
    }
  };
  const authHeaders = () => (token() ? { "x-auth-token": token() } : {});

  // The OTP route has failed: place the order without it. This toast is the
  // only place the customer is told this order will not be instant.
  const placeWithoutEc = (reason) => {
    if (placedRef.current || !openRef.current) return;
    placedRef.current = true;
    runRef.current += 1; // stop any poll still running
    setStage("placing");
    toast(msg(WITHOUT_OTP_KEYS[reason] || WITHOUT_OTP_KEYS["otp-not-sent"]), {
      icon: "⚠️",
    });
    onPlaceWithoutEc(reason);
  };

  // A failure on the way to the OTP: without the fallback, show it.
  const sendFailed = (reason, message) => {
    if (onPlaceWithoutEc) {
      placeWithoutEc(reason);
      return;
    }
    setStage("error");
    setNote(message);
    toast.error(message);
  };

  // No usable match (0, or over 100 documents): the customer edits the details.
  const showNoMatch = (item) => {
    setStage(item.stage);
    setDocCount(item.docCount || 0);
    setNote(item.message || "");
  };

  const showOtpBox = (nextStage, message) => {
    setStage(nextStage);
    setOtp("");
    setNote(message || msg("EC.OTP_SENT_TO", { mobile }));
    setHelpIn(OTP_HELP_AFTER_S);
    toast.success(msg("EC.OTP_SENT"));
  };

  // Start (sends the OTP in the background) and poll until it is out. Gives up
  // after SEND_WAIT_MS and places the order without the OTP.
  const sendOtp = async () => {
    const run = ++runRef.current;
    const live = () => openRef.current && runRef.current === run;
    setStage("sending");
    setOtp("");
    setNote("");
    const deadline = Date.now() + SEND_WAIT_MS;
    try {
      // A start request that hangs counts against the same wait.
      const res = await Promise.race([
        startTsEc({
          base: flow.base,
          body: startBody(),
        }).unwrap(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error(START_TIMEOUT)), SEND_WAIT_MS),
        ),
      ]);
      if (!live()) return;
      const item = res?.data?.item || {};
      const id = item.requestId;
      setRequestId(id);
      if (item.stage === "not-found" || item.stage === "too-many") {
        showNoMatch(item);
        return;
      }
      if (item.stage && item.stage !== "starting") {
        showOtpBox(item.stage, item.message);
        return;
      }
      while (live() && Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2500));
        if (!live()) return;
        let st = {};
        try {
          const s = await AxiosPropertyInstance.get(`/${flow.base}/status`, {
            params: { requestId: id },
            headers: authHeaders(),
            timeout: 10000,
          });
          st = s?.data?.data?.item || {};
        } catch {
          continue; // one failed poll is not a failed send — keep waiting
        }
        if (!live()) return;
        if (st.stage === "not-found" || st.stage === "too-many") {
          showNoMatch(st);
          return;
        }
        if (st.stage === "account-otp" || st.stage === "search-otp") {
          showOtpBox(st.stage, st.message);
          return;
        }
        if (st.stage === "error" || st.stage === "expired") {
          sendFailed(
            st.serverDown ? "server-down" : "otp-not-sent",
            st.error || st.message || msg("EC.SEND_FAILED"),
          );
          return;
        }
      }
      if (live()) sendFailed("otp-not-sent", msg("EC.TIMEOUT"));
    } catch (err) {
      if (!live()) return;
      sendFailed(
        err?.data?.status?.description === GOVT_SERVER_DOWN ? "server-down" : "otp-not-sent",
        err?.message === START_TIMEOUT
          ? msg("EC.TIMEOUT")
          : apiError(err, "EC.SEND_FAILED"),
      );
    }
  };

  // On open: is the portal up? Down → place without the OTP straight away.
  // Up (or the check itself failed — never block on it) → send the OTP once.
  useEffect(() => {
    if (!open) {
      startedRef.current = false;
      withoutPlotRef.current = false;
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;
    placedRef.current = false;
    if (needsPropertyDetail()) {
      setStage("needs-detail");
      return;
    }
    (async () => {
      if (flow.health && onPlaceWithoutEc) {
        setStage("checking");
        try {
          const h = await AxiosPropertyInstance.get(`/${flow.base}/health`, {
            headers: authHeaders(),
            timeout: 15000,
          });
          if (!openRef.current) return;
          if (h?.data?.data?.item?.up === false) {
            placeWithoutEc("server-down");
            return;
          }
        } catch {
          /* could not check — try the OTP; its own fallbacks still apply */
        }
        if (!openRef.current) return;
      }
      sendOtp();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const missingPropertyDetail = needsPropertyDetail();

  // Count down to "Didn't get the OTP?" while the OTP box is showing.
  const isOtp = stage === "account-otp" || stage === "search-otp";
  useEffect(() => {
    if (!isOtp || helpIn <= 0) return;
    const t = setTimeout(() => setHelpIn((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [isOtp, helpIn]);

  const handleOtp = async () => {
    if (otp.length < 4) {
      toast.error(msg("EC.ENTER_OTP"));
      return;
    }
    try {
      const res = await verifyTsEcOtp({ base: flow.base, body: { requestId, otp } }).unwrap();
      const item = res?.data?.item || {};
      if (item.stage === "search-otp") {
        setStage("search-otp");
        setOtp("");
        setNote(item.message || msg("EC.ACCOUNT_READY"));
        setHelpIn(OTP_HELP_AFTER_S);
        toast.success(msg("EC.ACCOUNT_READY"));
        return;
      }
      if (item.stage === "not-found" || item.stage === "too-many") {
        setStage(item.stage);
        setDocCount(item.docCount || 0);
        setNote(item.message || "");
        return;
      }
      if (item.stage === "done") {
        setStage("done");
        setNote(item.message || msg("EC.FETCHED"));
        toast.success(msg("EC.FETCHED"));
        // Hand the request id back so the caller places + completes the order
        // through the normal checkout (the EC url stays server-side).
        if (onEcReady) onEcReady(item.requestId || requestId);
        return;
      }
      toast.error(msg("COMMON.GENERIC"));
    } catch (err) {
      if (err?.data?.status?.description === GOVT_SERVER_DOWN && onPlaceWithoutEc) {
        placeWithoutEc("server-down");
        return;
      }
      // Wrong / expired OTP: let them resend or place without it right away.
      setHelpIn(0);
      toast.error(apiError(err, "EC.VERIFY_FAILED"));
    }
  };

  const busy = starting || verifying;
  const maskedMobile = mobile ? `••••••${String(mobile).slice(-4)}` : "your mobile";
  const otpTitle = stage === "account-otp" ? "Verify your number" : "Enter the EC OTP";
  const otpHint =
    stage === "account-otp"
      ? "One-time account setup — enter the code we sent."
      : "Enter the code we sent to fetch your Encumbrance Certificate.";

  return (
    <ModalWrapper
      open={open}
      onClose={onClose}
      className="absolute left-1/2 top-1/2 max-h-[90vh] -translate-x-1/2 -translate-y-1/2 overflow-y-auto outline-none"
    >
      <div className="relative w-[92vw] max-w-[420px] overflow-hidden rounded-2xl bg-white shadow-2xl">
        <button
          onClick={onClose}
          className="absolute right-3.5 top-3.5 z-10 rounded-full p-1 text-white/80 transition hover:bg-white/15 hover:text-white"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        {/* Header band */}
        <div className="bg-primary px-6 py-5 text-white">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15">
              <FileText size={18} />
            </span>
            <div>
              <h3 className="text-base font-semibold leading-tight">Instant EC</h3>
              <p className="text-xs text-white/80">{flow.label} · Encumbrance Certificate</p>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
            {property ? (
              <>
                {property.villageName && (
                  <span className="rounded-full bg-white/15 px-2 py-0.5">{property.villageName}</span>
                )}
                {property.surveyNo && (
                  <span className="rounded-full bg-white/15 px-2 py-0.5">Sy {property.surveyNo}</span>
                )}
                {property.plotNo && !withoutPlotRef.current && (
                  <span className="rounded-full bg-white/15 px-2 py-0.5">Plot {property.plotNo}</span>
                )}
                {property.houseNo && !withoutPlotRef.current && (
                  <span className="rounded-full bg-white/15 px-2 py-0.5">Door {property.houseNo}</span>
                )}
              </>
            ) : (
              <>
                <span className="rounded-full bg-white/15 px-2 py-0.5">Doc {docNo}/{year}</span>
                <span className="rounded-full bg-white/15 px-2 py-0.5">{sroName || `SRO ${sroCode}`}</span>
              </>
            )}
          </div>
        </div>

        <div className="p-6">
          {(stage === "checking" || stage === "sending" || stage === "placing") && (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <span className="text-primary">{spinner}</span>
              <div>
                <p className="text-sm font-medium text-gray-800">
                  {stage === "checking"
                    ? "Checking the government server…"
                    : stage === "placing"
                      ? "Placing your order…"
                      : "Sending OTP…"}
                </p>
                <p className="mt-0.5 text-xs text-gray-500">
                  {stage === "placing"
                    ? "Our team will fetch your EC and deliver it."
                    : stage === "sending"
                      ? `to ${maskedMobile}. This can take a few seconds.`
                      : "Just a moment."}
                </p>
              </div>
            </div>
          )}

          {isOtp && (
            <div className="flex flex-col items-center gap-4">
              <div className="text-center">
                <div className="mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-primary/10">
                  <ShieldCheck className="text-primary" size={22} />
                </div>
                <p className="text-sm font-semibold text-gray-900">{otpTitle}</p>
                <p className="mt-0.5 text-xs text-gray-500">
                  {otpHint} Sent to <span className="font-medium text-gray-700">{maskedMobile}</span>.
                </p>
              </div>

              <InputOTP maxLength={6} value={otp} onChange={setOtp} autoFocus>
                <InputOTPGroup className="flex justify-center gap-2">
                  {[...Array(6)].map((_, i) => (
                    <InputOTPSlot
                      key={i}
                      index={i}
                      className="h-12 w-11 rounded-xl border-2 border-gray-200 text-lg font-bold text-primary data-[active]:border-primary data-[active]:ring-2 data-[active]:ring-primary/20"
                    >
                      {otp[i] || ""}
                    </InputOTPSlot>
                  ))}
                </InputOTPGroup>
              </InputOTP>

              <button
                onClick={handleOtp}
                disabled={busy || otp.length < 6}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 text-sm font-semibold text-white transition hover:opacity-95 disabled:opacity-50"
              >
                {busy && spinner}
                {stage === "account-otp" ? "Verify & continue" : "Verify & fetch EC"}
              </button>
              {stage === "account-otp" && (
                <p className="text-center text-[11px] text-gray-400">
                  First time for this number — one more OTP follows to fetch the EC.
                </p>
              )}

              {helpIn > 0 ? (
                <p className="text-center text-[11px] text-gray-400">
                  Didn&apos;t get the OTP? Options in {helpIn}s
                </p>
              ) : (
                <div className="w-full rounded-xl border border-gray-200 p-3 text-center">
                  <p className="text-xs font-medium text-gray-700">Didn&apos;t get the OTP?</p>
                  <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                    <button
                      onClick={sendOtp}
                      disabled={busy}
                      className="flex-1 rounded-lg border border-primary px-3 py-2 text-xs font-semibold text-primary transition hover:bg-primary/5 disabled:opacity-50"
                    >
                      Resend OTP
                    </button>
                    {onPlaceWithoutEc && (
                      <button
                        onClick={() => placeWithoutEc("otp-not-received")}
                        disabled={busy}
                        className="flex-1 rounded-lg bg-gray-900 px-3 py-2 text-xs font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
                      >
                        Place order without OTP
                      </button>
                    )}
                  </div>
                  {onPlaceWithoutEc && (
                    <p className="mt-2 text-[11px] text-gray-400">
                      Without OTP, our team fetches your EC and delivers it.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {stage === "done" && (
            <div className="flex flex-col items-center gap-3 py-5 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-50">
                <ShieldCheck className="text-green-600" size={30} />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">EC verified</p>
                <p className="mt-0.5 text-xs text-gray-500">{note || "Fetching your EC and placing the order…"}</p>
              </div>
            </div>
          )}

          {stage === "needs-detail" && missingPropertyDetail && (
            <div className="flex flex-col items-center gap-4 py-3 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-50">
                <FileText className="text-amber-600" size={24} />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Add the plot or door number</p>
                <p className="mt-1 text-xs leading-relaxed text-gray-600">
                  Enter the plot number or the door/house number to get the EC instantly. A survey number alone matches too many documents.
                </p>
                <p className="mt-1 text-[11px] text-gray-400">No payment has been taken.</p>
              </div>
              <button
                onClick={onClose}
                className="w-full rounded-xl bg-primary py-3 text-sm font-semibold text-white transition hover:opacity-95"
              >
                Edit details
              </button>
            </div>
          )}

          {(stage === "not-found" || stage === "too-many") && (
            <div className="flex flex-col items-center gap-4 py-3 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-50">
                <FileText className="text-amber-600" size={24} />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {stage === "not-found" ? "No matching property found" : `${docCount} documents match`}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-gray-600">
                  {stage === "not-found"
                    ? isAp && property?.surveyNo && (withoutPlotRef.current || (!property?.plotNo && !property?.houseNo))
                      ? `The registry has no documents for survey ${property.surveyNo} in this village. Check the village and survey number exactly as written in your deed.`
                      : "The registry has no property with these details. Check the plot / door number exactly as written in your deed."
                    : "That's too many for one EC. Add or correct the plot / door number to narrow the search."}
                </p>
                <p className="mt-1 text-[11px] text-gray-400">No payment has been taken.</p>
              </div>
              {stage === "not-found" &&
                property?.surveyNo &&
                (property.plotNo || property.houseNo) &&
                !withoutPlotRef.current && (
                  <button
                    onClick={() => {
                      withoutPlotRef.current = true;
                      sendOtp();
                    }}
                    disabled={busy}
                    className="w-full rounded-xl bg-primary py-3 text-sm font-semibold text-white transition hover:opacity-95 disabled:opacity-50"
                  >
                    Search survey {property.surveyNo} without plot number
                  </button>
                )}
              {stage === "not-found" &&
                property?.surveyNo &&
                (property.plotNo || property.houseNo) &&
                !withoutPlotRef.current && (
                <p className="-mt-2 text-[11px] text-gray-400">
                  {isAp
                    ? `An OTP goes to ${maskedMobile} only if documents are found.`
                    : `Sends a new OTP to ${maskedMobile}.`}
                </p>
              )}
              <button
                onClick={onClose}
                className="w-full rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Edit details
              </button>
            </div>
          )}

          {stage === "error" && (
            <div className="flex flex-col items-center gap-4 py-4 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
                <X className="text-red-500" size={26} />
              </div>
              <p className="text-sm text-red-600">{note}</p>
              <button
                onClick={onClose}
                className="rounded-xl border border-gray-200 px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </ModalWrapper>
  );
}
